import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from "react";
import './Checkbox.scss';

const CHECKBOX_STATES = {
  CHECKED: 'checked',
  UNCHECKED: 'unchecked',
  UNDETERMINED: 'undetermined',
};

/**
 * @typedef CheckboxCustom
 * @prop {('checked'|'unchecked'|'undetermined')} defaultStatus
 *
 * @typedef {import('react').InputHTMLAttributes & CheckboxCustom} CheckboxProps
 */


/**
 * @param {CheckboxProps} params
 * @returns
 */
const Checkbox = ({
  value,
  defaultStatus,
  disabled,
  name,
  id,
  label,
  threeState = false,
  onChange,
  onBlur,
  ...props
}) => {
  const [status, setStatus] = useState(value ? CHECKBOX_STATES.CHECKED : CHECKBOX_STATES.UNCHECKED);
  const [iconStatus, setIconStatus] = useState();

  useEffect(() => {
    if (value === null) {
      setStatusUndetermined();
    } else {
      value ? setStatusChecked() : setStatusUnchecked();
    }
  }, [value]);

  const setStatusUndetermined = useCallback(() => {
    setIconStatus('fa-minus');
    setStatus(CHECKBOX_STATES.UNDETERMINED);
    return CHECKBOX_STATES.UNDETERMINED;
  }, []);

  const setStatusChecked = useCallback(() => {
    setIconStatus('fa-check');
    setStatus(CHECKBOX_STATES.CHECKED);
    return CHECKBOX_STATES.CHECKED;
  }, []);

  const setStatusUnchecked = useCallback(() => {
    setIconStatus(null);
    setStatus(CHECKBOX_STATES.UNCHECKED);
    return CHECKBOX_STATES.UNCHECKED;
  }, []);

  useEffect(() => {
    if (!value) {
      if (defaultStatus === CHECKBOX_STATES.UNDETERMINED) {
        setStatusUndetermined();
      } else if (defaultStatus === CHECKBOX_STATES.CHECKED) {
        setStatusChecked();
      }
    } else {
      setStatusChecked();
    }
  }, []);

  const handleClick = useCallback((e) => {
    if (e.target.tagName === 'A') return;

    let newStatus = CHECKBOX_STATES.UNCHECKED;

    if (threeState) {
      if (status === CHECKBOX_STATES.CHECKED) { // STATUS CHECKED
        newStatus = setStatusUnchecked();
      } else if (status === CHECKBOX_STATES.UNDETERMINED) { // STATUS UNDETERMINED
        newStatus = setStatusChecked();
      } else { // STATUS UNCHECKED
        newStatus = setStatusUndetermined();
      }
      if (onChange) onChange(newStatus);
      if (onBlur) onBlur(newStatus);
    } else {
      // TWO STATES CHECKBOX
      newStatus = status === CHECKBOX_STATES.CHECKED ? setStatusUnchecked() : setStatusChecked();
      e.target.value = newStatus === CHECKBOX_STATES.CHECKED;
      e.target.name = name;
      if (onChange) onChange(e);
      if (onBlur) onBlur(e);
    }

  }, [onChange, onBlur, status, value]);

  return (
    <div className={classNames("checkbox", { disabled })}>
      <div className={classNames('checker', status)}>
        <input type="checkbox"
          name={name}
          disabled={disabled}
          id={name}
          checked={status == CHECKBOX_STATES.CHECKED}
          onChange={handleClick}
          onBlur={onBlur}
          {...props}
        />
        {iconStatus && <i className={classNames("fal", iconStatus)}></i>}
      </div>
      {label && <label className='form-label' htmlFor={id} required={props.required}>{label}</label>}
    </div>
  );
};

Checkbox.propTypes = {
  value: PropTypes.bool,
  defaultStatus: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  threeState: PropTypes.bool,
  name: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.any,
  required: PropTypes.bool,
};

Checkbox.defaultProps = {
  value: false,
  defaultStatus: CHECKBOX_STATES.UNCHECKED,
  onChange: null,
  onBlur: null,
  disabled: false,
  threeState: false,
  name: null,
  id: null,
  label: null,
  required: false,
};

export default React.memo(Checkbox);
